import {
  Alert,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertIcon,
  Box,
  Button,
  Code,
  HStack,
  Progress,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useRef } from 'react'
import type { RefObject } from 'react'
import type { TFunction } from 'i18next'
import { ChainBadge } from '@/components/common/ChainBadge'
import { InlineSkeleton } from '@/components/common/LoadingSkeleton'
import type { ContractRegistryRecord } from '@/types'
import {
  getRegistryActionDisplayAddresses,
  type DeploymentDisplayAddressKind,
} from '@/utils/adminAddressPresentation'

interface DeployDialogProps {
  isOpen: boolean
  onClose: () => void
  selectedRecord: ContractRegistryRecord | null
  isDeploying: boolean
  confirmDeploy: () => Promise<void> | void
  textColor: string
  modalBg: string
  borderColor: string
  getFormattedAmount: (record: ContractRegistryRecord) => { amountText: string; symbol: string }
  resolveRegistryChainId: (record?: ContractRegistryRecord | null) => string
  t: TFunction
  cancelRef?: RefObject<HTMLButtonElement>
}

export const DeployDialog = ({
  isOpen,
  onClose,
  selectedRecord,
  isDeploying,
  confirmDeploy,
  textColor,
  modalBg,
  borderColor,
  getFormattedAmount,
  resolveRegistryChainId,
  t,
  cancelRef,
}: DeployDialogProps) => {
  const localCancelRef = useRef<HTMLButtonElement>(null)
  const resolvedCancelRef = cancelRef ?? localCancelRef
  const addressRows = selectedRecord ? getRegistryActionDisplayAddresses(selectedRecord) : []
  const hasAddressMismatch = addressRows.some(item => item.isMismatch)
  const addressLabels: Record<DeploymentDisplayAddressKind, string> = {
    payment: t('contractRegistry.deployment.details.paymentAddress', 'Payment Address'),
    contract: t('contractRegistry.dialogs.deploy.contractAddress'),
    predicted_contract: t('contractRegistry.deployment.details.predictedAddress'),
    deployed_contract: t('contractRegistry.deployment.details.deployedAddress'),
    vault_token_account: t(
      'contractRegistry.deployment.details.vaultTokenAccount',
      'Vault Token Account'
    ),
  }

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={resolvedCancelRef}
      onClose={() => {
        if (!isDeploying) {
          onClose()
        }
      }}
      closeOnOverlayClick={!isDeploying}
      closeOnEsc={!isDeploying}
    >
      <AlertDialogOverlay>
        <AlertDialogContent maxW="md">
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            <HStack spacing={3}>
              <Text>{t('contractRegistry.dialogs.deploy.title')}</Text>
              {isDeploying && <InlineSkeleton isCircle width="16px" height="16px" />}
            </HStack>
          </AlertDialogHeader>

          <AlertDialogBody>
            {selectedRecord && (
              <VStack spacing={4} align="stretch">
                {!isDeploying ? (
                  <Text color={textColor}>{t('contractRegistry.dialogs.deploy.confirmation')}</Text>
                ) : (
                  <Alert status="info">
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="medium">{t('contractRegistry.dialogs.deploy.deploymentInProgress')}</Text>
                      <Text fontSize="sm" mt={1}>
                        {t('contractRegistry.dialogs.deploy.deploymentInProgressDesc')}
                      </Text>
                    </Box>
                  </Alert>
                )}

                <Box bg={modalBg} p={4} borderRadius="md" border="1px" borderColor={borderColor}>
                  <VStack spacing={3} align="stretch" fontSize="sm">
                    <HStack justify="space-between">
                      <Text fontWeight="medium" color={textColor}>
                        {t('contractRegistry.dialogs.deploy.orderId')}:
                      </Text>
                      <Code fontSize="xs">{selectedRecord.order_id}</Code>
                    </HStack>
                    {addressRows.length > 0 ? addressRows.map(item => (
                      <HStack justify="space-between" align="start" key={item.kind}>
                        <Text fontWeight="medium" color={textColor}>
                          {addressLabels[item.kind]}:
                        </Text>
                        <Code fontSize="xs" maxW="250px" wordBreak="break-all">
                          {item.address}
                        </Code>
                      </HStack>
                    )) : (
                      <HStack justify="space-between" align="start">
                        <Text fontWeight="medium" color={textColor}>
                          {t('contractRegistry.deployment.details.paymentAddress', 'Payment Address')}:
                        </Text>
                        <Code fontSize="xs" maxW="250px" wordBreak="break-all">
                          {t('common.na')}
                        </Code>
                      </HStack>
                    )}
                    <HStack justify="space-between">
                      <Text fontWeight="medium" color={textColor}>
                        {t('contractRegistry.dialogs.deploy.amount')}:
                      </Text>
                      {(() => {
                        const { amountText, symbol } = getFormattedAmount(selectedRecord)
                        return (
                          <Text fontWeight="medium">
                            {amountText} {symbol}
                          </Text>
                        )
                      })()}
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontWeight="medium" color={textColor}>
                        {t('contractRegistry.dialogs.deploy.chain')}:
                      </Text>
                      <ChainBadge chainId={resolveRegistryChainId(selectedRecord) || 'unknown'} size="sm" />
                    </HStack>
                  </VStack>
                </Box>

                {hasAddressMismatch && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <Text fontSize="sm">
                      {t(
                        'contractRegistry.dialogs.deploy.addressMismatch',
                        'The payment address and predicted deployment target do not match.'
                      )}
                    </Text>
                  </Alert>
                )}

                {isDeploying && (
                  <Box>
                    <VStack spacing={3} align="stretch">
                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={2}>
                          {t('contractRegistry.dialogs.deploy.deploying')}
                        </Text>
                        <Progress isIndeterminate colorScheme="blue" size="sm" borderRadius="md" />
                      </Box>
                      <Text fontSize="xs" color={textColor} textAlign="center">
                        {t('contractRegistry.dialogs.deploy.deploymentNote')}
                      </Text>
                    </VStack>
                  </Box>
                )}
              </VStack>
            )}
          </AlertDialogBody>

          <AlertDialogFooter>
            <Button ref={resolvedCancelRef} onClick={onClose} isDisabled={isDeploying} variant="ghost" size="md">
              {t('common.cancel')}
            </Button>
            <Button
              colorScheme="green"
              onClick={confirmDeploy}
              ml={3}
              isLoading={isDeploying}
              loadingText={t('contractRegistry.dialogs.deploy.deployingButton')}
              isDisabled={isDeploying}
              size="md"
            >
              {isDeploying
                ? t('contractRegistry.dialogs.deploy.deployingButton')
                : t('contractRegistry.actions.deploy')}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}
